// src/pages/StockTransferPage.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { apiUrl } from "../config/api";

type LocationInfo = {
  id: number;
  code: string;
  label: string;
};

type StockCardItem = {
  product_code: string;
  product_name: string;
  unit: string | null;
  stock_by_location: Record<string, number>;
  total_stock: number;
  stock_on_hand: number;
  imageUrl?: string;
};

type StockByLocationResponse = {
  items?: unknown;
  locations?: unknown;
  page?: number;
  limit?: number;
  total?: number;
  error?: string;
};

type LotDetailItem = {
  id: number;
  product_code: string;
  product_name: string;
  location_code: string | null;
  location_label: string | null;
  qty: number;
  available_qty: number;
  unit_cost: number;
  ref_table: string | null;
  ref_no: string | null;
  created_at: string;
  source_movement_id: number | null;
};

type LotsResponse = {
  ok?: boolean;
  product_code?: string;
  items?: unknown;
  error?: string;
};

type TransferHistoryItem = {
  id: number;
  product_code: string;
  product_name: string;
  qty: number;
  unit_cost: number;
  source_inventory_movement_id: number;
  destination_inventory_movement_id: number;
};

type TransferHistory = {
  id: number;
  transfer_no: string;
  from_location_code: string;
  from_location_label: string;
  to_location_code: string;
  to_location_label: string;
  note: string | null;
  created_at: string;
  items: TransferHistoryItem[];
};

type StockTransfersResponse = {
  items?: unknown;
  page?: number;
  limit?: number;
  total?: number;
  error?: string;
};

type CreateTransferResponse = {
  ok?: boolean;
  transfer?: unknown;
  error?: string;
  meta?: unknown;
};

type TransferDraft = {
  productCode: string;
  fromLocationCode: string;
  toLocationCode: string;
  qty: number;
  note: string;
  costReceived: boolean;
  costReturned: boolean;
};

type StockFilterMode = "ALL" | "TIKTOK_GT_ONE" | "LOW_TOTAL_WITH_TIKTOK";

type ActiveTab = "transfer" | "lots" | "history";

function toLocationInfo(row: unknown): LocationInfo | null {
  if (!row || typeof row !== "object") return null;
  const data = row as Record<string, unknown>;
  const id = Number(data.id ?? 0);
  const code = typeof data.code === "string" ? data.code.trim().toUpperCase() : "";
  const label = typeof data.label === "string" ? data.label.trim() : "";
  if (!id || !code || !label) return null;
  return { id, code, label };
}

function toStockCardItem(row: unknown): StockCardItem | null {
  if (!row || typeof row !== "object") return null;
  const data = row as Record<string, unknown>;
  const product_code = typeof data.product_code === "string" ? data.product_code.trim() : "";
  const product_name = typeof data.product_name === "string" ? data.product_name.trim() : "";
  if (!product_code || !product_name) return null;

  const stockRaw =
    data.stock_by_location && typeof data.stock_by_location === "object"
      ? (data.stock_by_location as Record<string, unknown>)
      : {};

  const stock_by_location = Object.fromEntries(
    Object.entries(stockRaw).map(([key, value]) => [
      key.toUpperCase(),
      Number.isFinite(Number(value)) ? Number(value) : 0,
    ])
  );

  return {
    product_code,
    product_name,
    unit: typeof data.unit === "string" && data.unit.trim() ? data.unit.trim() : null,
    stock_by_location,
    total_stock: Number(data.total_stock ?? 0),
    stock_on_hand: Number(data.stock_on_hand ?? 0),
    imageUrl: typeof data.imageUrl === "string" && data.imageUrl.trim() ? data.imageUrl.trim() : undefined,
  };
}

function toLotDetailItem(row: unknown): LotDetailItem | null {
  if (!row || typeof row !== "object") return null;
  const data = row as Record<string, unknown>;
  const id = Number(data.id ?? 0);
  const product_code = typeof data.product_code === "string" ? data.product_code.trim() : "";
  if (!id || !product_code) return null;

  return {
    id,
    product_code,
    product_name: typeof data.product_name === "string" ? data.product_name.trim() : "",
    location_code:
      typeof data.location_code === "string" && data.location_code.trim()
        ? data.location_code.trim().toUpperCase()
        : null,
    location_label:
      typeof data.location_label === "string" && data.location_label.trim()
        ? data.location_label.trim()
        : null,
    qty: Number(data.qty ?? 0),
    available_qty: Number(data.available_qty ?? 0),
    unit_cost: Number(data.unit_cost ?? 0),
    ref_table: typeof data.ref_table === "string" && data.ref_table.trim() ? data.ref_table.trim() : null,
    ref_no: typeof data.ref_no === "string" && data.ref_no.trim() ? data.ref_no.trim() : null,
    created_at: typeof data.created_at === "string" ? data.created_at : "",
    source_movement_id: data.source_movement_id == null ? null : Number(data.source_movement_id),
  };
}

function toTransferHistoryItem(row: unknown): TransferHistoryItem | null {
  if (!row || typeof row !== "object") return null;
  const data = row as Record<string, unknown>;
  const id = Number(data.id ?? 0);
  const product_code = typeof data.product_code === "string" ? data.product_code.trim() : "";
  if (!id || !product_code) return null;

  return {
    id,
    product_code,
    product_name: typeof data.product_name === "string" ? data.product_name.trim() : "",
    qty: Number(data.qty ?? 0),
    unit_cost: Number(data.unit_cost ?? 0),
    source_inventory_movement_id: Number(data.source_inventory_movement_id ?? 0),
    destination_inventory_movement_id: Number(data.destination_inventory_movement_id ?? 0),
  };
}

function toTransferHistory(row: unknown): TransferHistory | null {
  if (!row || typeof row !== "object") return null;
  const data = row as Record<string, unknown>;
  const id = Number(data.id ?? 0);
  const transfer_no = typeof data.transfer_no === "string" ? data.transfer_no.trim() : "";
  if (!id || !transfer_no) return null;

  const itemsRaw = Array.isArray(data.items) ? data.items : [];

  return {
    id,
    transfer_no,
    from_location_code:
      typeof data.from_location_code === "string" ? data.from_location_code.trim().toUpperCase() : "",
    from_location_label:
      typeof data.from_location_label === "string" ? data.from_location_label.trim() : "",
    to_location_code:
      typeof data.to_location_code === "string" ? data.to_location_code.trim().toUpperCase() : "",
    to_location_label:
      typeof data.to_location_label === "string" ? data.to_location_label.trim() : "",
    note: typeof data.note === "string" && data.note.trim() ? data.note.trim() : null,
    created_at: typeof data.created_at === "string" ? data.created_at : "",
    items: itemsRaw
      .map(toTransferHistoryItem)
      .filter((item): item is TransferHistoryItem => item != null),
  };
}

function getFriendlyError(code: string): string {
  switch (code) {
    case "product_code_required":
      return "กรุณาเลือกสินค้าที่ต้องการย้าย";
    case "from_location_code_required":
      return "กรุณาเลือกกองต้นทาง";
    case "to_location_code_required":
      return "กรุณาเลือกกองปลายทาง";
    case "same_source_and_destination":
      return "กองต้นทางและปลายทางต้องไม่ใช่กองเดียวกัน";
    case "invalid_qty":
      return "จำนวนที่ย้ายต้องมากกว่า 0";
    case "product_not_found":
      return "ไม่พบสินค้าในระบบ";
    case "stock_not_enough":
      return "สต็อกต้นทางไม่พอสำหรับการย้าย";
    case "failed_to_fetch_stock_by_location":
      return "โหลด stock แยกตามกองไม่สำเร็จ";
    case "failed_to_fetch_stock_lots":
      return "โหลดรายละเอียด lot ไม่สำเร็จ";
    case "failed_to_fetch_stock_transfers":
      return "โหลดประวัติการย้ายไม่สำเร็จ";
    default:
      return code || "เกิดข้อผิดพลาดระหว่างจัดการ stock";
  }
}

function getSafeUnitLabel(unit: string | null): string {
  if (!unit || !unit.trim()) return "ชิ้น";
  const trimmed = unit.trim();
  if (/(เธ|เน|โ€|�)/.test(trimmed)) return "ชิ้น";
  if (/^[A-Za-z0-9\s._/-]+$/.test(trimmed)) return "ชิ้น";
  if (/[฀-๿]/.test(trimmed)) return trimmed;
  return "ชิ้น";
}

function locationTone(code: string): string {
  switch (code) {
    case "MAIN":
      return "border-primary-subtle bg-primary-subtle";
    case "SHOP":
      return "border-success-subtle bg-success-subtle";
    case "TIKTOK":
      return "border-warning-subtle bg-warning-subtle";
    case "USA":
      return "border-danger-subtle bg-danger-subtle";
    default:
      return "border-secondary-subtle bg-light";
  }
}

function locationBadgeClass(code: string): string {
  switch (code) {
    case "MAIN":
      return "badge bg-primary";
    case "SHOP":
      return "badge bg-success";
    case "TIKTOK":
      return "badge bg-warning text-dark";
    case "USA":
      return "badge bg-danger";
    default:
      return "badge bg-secondary";
  }
}

export default function StockTransferPage(): JSX.Element {
  // --- Shared data ---
  const [locations, setLocations] = useState<LocationInfo[]>([]);
  const [items, setItems] = useState<StockCardItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // --- Tab ---
  const [activeTab, setActiveTab] = useState<ActiveTab>("transfer");

  // --- Tab 1: Stock table ---
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [lowStockInput, setLowStockInput] = useState("");
  const [stockFilterMode, setStockFilterMode] = useState<StockFilterMode>("ALL");
  const [priorityLocation, setPriorityLocation] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [total, setTotal] = useState(0);
  const [transferMessage, setTransferMessage] = useState("");
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  // --- Transfer Modal ---
  const [modalOpen, setModalOpen] = useState(false);
  const [modalProduct, setModalProduct] = useState<StockCardItem | null>(null);
  const [activeTransfer, setActiveTransfer] = useState<TransferDraft | null>(null);
  const [transferSubmitting, setTransferSubmitting] = useState(false);
  const [transferError, setTransferError] = useState("");
  const [dragFromLoc, setDragFromLoc] = useState<string | null>(null);
  const [dragOverLoc, setDragOverLoc] = useState<string | null>(null);
  const qtyInputRef = useRef<HTMLInputElement>(null);

  // --- Tab 2: Lot view ---
  const [lotSearchInput, setLotSearchInput] = useState("");
  const [lotSearchQuery, setLotSearchQuery] = useState("");
  const [selectedLotCode, setSelectedLotCode] = useState("");
  const [lotMap, setLotMap] = useState<Record<string, LotDetailItem[]>>({});
  const [lotLoadingCode, setLotLoadingCode] = useState("");
  const [lotErrorMap, setLotErrorMap] = useState<Record<string, string>>({});

  // --- Tab 3: History ---
  const [historyItems, setHistoryItems] = useState<TransferHistory[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLimit] = useState(10);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const totalHistoryPages = Math.max(1, Math.ceil(historyTotal / historyLimit));
  const lowStockThreshold =
    lowStockInput.trim() === "" ? null : Math.max(0, Math.floor(Number(lowStockInput) || 0));
  const locationCodes = useMemo(() => locations.map((l) => l.code), [locations]);

  const visibleItems = useMemo(() => {
    if (stockFilterMode === "ALL") return items;
    if (stockFilterMode === "TIKTOK_GT_ONE") {
      return items.filter((item) => Number(item.stock_by_location.TIKTOK ?? 0) > 1);
    }
    if (lowStockThreshold == null) return [];
    return items.filter((item) => {
      const tiktokQty = Number(item.stock_by_location.TIKTOK ?? 0);
      return tiktokQty > 1 && item.total_stock < lowStockThreshold;
    });
  }, [items, lowStockThreshold, stockFilterMode]);

  const lotSearchResults = useMemo(() => {
    if (!lotSearchQuery.trim()) return items.slice(0, 40);
    const q = lotSearchQuery.trim().toLowerCase();
    return items
      .filter(
        (item) =>
          item.product_code.toLowerCase().includes(q) ||
          item.product_name.toLowerCase().includes(q)
      )
      .slice(0, 40);
  }, [items, lotSearchQuery]);

  const currentFromStock = useMemo(() => {
    if (!modalProduct || !activeTransfer) return 0;
    return Number(modalProduct.stock_by_location[activeTransfer.fromLocationCode] ?? 0);
  }, [modalProduct, activeTransfer]);

  const selectedLotGrouped = useMemo(() => {
    if (!selectedLotCode) return {};
    const rows = lotMap[selectedLotCode] ?? [];
    return rows.reduce<Record<string, LotDetailItem[]>>((acc, lot) => {
      const key = lot.location_code ?? "UNKNOWN";
      if (!acc[key]) acc[key] = [];
      acc[key].push(lot);
      return acc;
    }, {});
  }, [selectedLotCode, lotMap]);

  async function loadStock(pageToLoad = 1, query = searchQuery, sortLoc = priorityLocation): Promise<void> {
    try {
      setLoading(true);
      setError("");

      const url = new URL(apiUrl("/stock-by-location"));
      url.searchParams.set("page", String(pageToLoad));
      url.searchParams.set("limit", String(limit));
      if (query.trim()) {
        url.searchParams.set("q", query.trim());
      }
      if (sortLoc) {
        url.searchParams.set("sortLocation", sortLoc);
      }

      const resp = await fetch(url.toString(), { headers: { Accept: "application/json" } });
      const data = (await resp.json().catch(() => ({}))) as StockByLocationResponse;
      if (!resp.ok) {
        throw new Error(data.error ?? "failed_to_fetch_stock_by_location");
      }

      const nextLocations = Array.isArray(data.locations)
        ? data.locations
            .map(toLocationInfo)
            .filter((item): item is LocationInfo => item != null)
        : [];
      const nextItems = Array.isArray(data.items)
        ? data.items.map(toStockCardItem).filter((item): item is StockCardItem => item != null)
        : [];

      setLocations(nextLocations);
      setItems(nextItems);
      setPage(Number(data.page ?? pageToLoad));
      setTotal(Number(data.total ?? 0));
    } catch (err) {
      const code = err instanceof Error ? err.message : "failed_to_fetch_stock_by_location";
      setError(getFriendlyError(code));
    } finally {
      setLoading(false);
    }
  }

  async function loadHistory(pageToLoad = 1): Promise<void> {
    try {
      setHistoryLoading(true);
      setHistoryError("");

      const url = new URL(apiUrl("/stock-transfers"));
      url.searchParams.set("page", String(pageToLoad));
      url.searchParams.set("limit", String(historyLimit));

      const resp = await fetch(url.toString(), { headers: { Accept: "application/json" } });
      const data = (await resp.json().catch(() => ({}))) as StockTransfersResponse;
      if (!resp.ok) {
        throw new Error(data.error ?? "failed_to_fetch_stock_transfers");
      }

      setHistoryItems(
        Array.isArray(data.items)
          ? data.items
              .map(toTransferHistory)
              .filter((item): item is TransferHistory => item != null)
          : []
      );
      setHistoryPage(Number(data.page ?? pageToLoad));
      setHistoryTotal(Number(data.total ?? 0));
    } catch (err) {
      const code = err instanceof Error ? err.message : "failed_to_fetch_stock_transfers";
      setHistoryError(getFriendlyError(code));
    } finally {
      setHistoryLoading(false);
    }
  }

  async function loadLots(productCode: string): Promise<void> {
    try {
      setLotLoadingCode(productCode);
      setLotErrorMap((prev) => ({ ...prev, [productCode]: "" }));

      const resp = await fetch(
        apiUrl(`/stock-by-location/${encodeURIComponent(productCode)}/lots`),
        { headers: { Accept: "application/json" } }
      );
      const data = (await resp.json().catch(() => ({}))) as LotsResponse;
      if (!resp.ok || !data.ok) {
        throw new Error(data.error ?? "failed_to_fetch_stock_lots");
      }

      const nextLots = Array.isArray(data.items)
        ? data.items
            .map(toLotDetailItem)
            .filter((item): item is LotDetailItem => item != null)
        : [];

      setLotMap((prev) => ({ ...prev, [productCode]: nextLots }));
    } catch (err) {
      const code = err instanceof Error ? err.message : "failed_to_fetch_stock_lots";
      setLotErrorMap((prev) => ({ ...prev, [productCode]: getFriendlyError(code) }));
    } finally {
      setLotLoadingCode("");
    }
  }

  useEffect(() => {
    void loadStock(1, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void loadStock(1, searchQuery, priorityLocation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priorityLocation]);

  useEffect(() => {
    if (activeTab === "history" && !historyLoaded) {
      void loadHistory(1);
      setHistoryLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Auto-hide transfer success toast
  useEffect(() => {
    if (!transferMessage) return;
    const t = setTimeout(() => setTransferMessage(""), 3000);
    return () => clearTimeout(t);
  }, [transferMessage]);

  function handleSearch(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const nextQuery = searchInput.trim();
    setSearchQuery(nextQuery);
    setPage(1);
    void loadStock(1, nextQuery);
  }

  function handleLotSearch(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const q = lotSearchInput.trim();
    setLotSearchQuery(q);
    // Also load into items if not already matching
    if (q !== searchQuery) {
      setSearchQuery(q);
      setSearchInput(q);
      void loadStock(1, q);
    }
    setSelectedLotCode("");
  }

  // PMN-CRITICAL(/StockTransferPage): initializes real transfer payload defaults, including USA cost side-effect flags.
  function openModal(product: StockCardItem) {
    setTransferError("");
    setTransferMessage("");
    setModalProduct(product);

    // Auto-select from = location with highest stock
    let fromCode = locations[0]?.code ?? "";
    let maxQty = -1;
    for (const loc of locations) {
      const qty = Number(product.stock_by_location[loc.code] ?? 0);
      if (qty > maxQty) {
        maxQty = qty;
        fromCode = loc.code;
      }
    }
    const toCode = locations.find((l) => l.code !== fromCode)?.code ?? "";

    setActiveTransfer({
      productCode: product.product_code,
      fromLocationCode: fromCode,
      toLocationCode: toCode,
      qty: 1,
      note: "",
      costReceived: toCode === "USA",
      costReturned: fromCode === "USA",
    });
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setModalProduct(null);
    setActiveTransfer(null);
    setTransferError("");
  }

  // PMN-CRITICAL(/StockTransferPage): posts a real stock movement; success must refresh stock, history, and selected lot state.
  async function submitTransfer(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!activeTransfer) return;
    setTransferError("");

    try {
      setTransferSubmitting(true);

      const resp = await fetch(apiUrl("/stock-transfers"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          product_code: activeTransfer.productCode,
          from_location_code: activeTransfer.fromLocationCode,
          to_location_code: activeTransfer.toLocationCode,
          qty: activeTransfer.qty,
          note: activeTransfer.note.trim() || undefined,
          cost_received: activeTransfer.toLocationCode === "USA" ? activeTransfer.costReceived : undefined,
          cost_returned: activeTransfer.fromLocationCode === "USA" ? activeTransfer.costReturned : undefined,
        }),
      });

      const data = (await resp.json().catch(() => ({}))) as CreateTransferResponse;
      if (!resp.ok || !data.ok) {
        throw new Error(data.error ?? "failed_to_create_stock_transfer");
      }

      setTransferMessage(
        `ย้าย ${activeTransfer.productCode} จำนวน ${activeTransfer.qty} ${getSafeUnitLabel(modalProduct?.unit ?? null)} จาก ${activeTransfer.fromLocationCode} → ${activeTransfer.toLocationCode} เรียบร้อยแล้ว`
      );
      closeModal();
      await loadStock(page, searchQuery);
      if (historyLoaded) {
        void loadHistory(1);
        setHistoryPage(1);
      }
      if (selectedLotCode === activeTransfer.productCode) {
        void loadLots(activeTransfer.productCode);
      }
    } catch (err) {
      const code = err instanceof Error ? err.message : "failed_to_create_stock_transfer";
      setTransferError(getFriendlyError(code));
    } finally {
      setTransferSubmitting(false);
    }
  }

  function selectLotProduct(code: string) {
    setSelectedLotCode(code);
    if (!lotMap[code]) void loadLots(code);
  }

  return (
    <div className="min-vh-100 bg-light">
      {/* Toast */}
      <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1080 }}>
        {transferMessage && (
          <div className="alert alert-success shadow mb-0 rounded-3 py-2 px-3">
            {transferMessage}
          </div>
        )}
      </div>

      <div className="container py-3 py-md-4" style={{ maxWidth: 1080 }}>
        {/* Header */}
        <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
          <div>
            <h4 className="mb-0 text-primary">จัดการ Stock</h4>
            <div className="text-muted small">ย้าย stock ระหว่าง MAIN / SHOP / TIKTOK / USA</div>
          </div>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => {
                void loadStock(page, searchQuery);
                if (selectedLotCode) void loadLots(selectedLotCode);
                if (historyLoaded) void loadHistory(historyPage);
              }}
            >
              รีเฟรช
            </button>
            <Link to="/" className="btn btn-outline-primary btn-sm">
              กลับหน้าเมนู
            </Link>
          </div>
        </div>

        {/* Tabs */}
        <ul className="nav nav-tabs mb-0">
          {(
            [
              { key: "transfer", label: "ย้าย Stock" },
              { key: "lots", label: "ดู Lot" },
              { key: "history", label: "ประวัติ" },
            ] as { key: ActiveTab; label: string }[]
          ).map(({ key, label }) => (
            <li key={key} className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === key ? "active" : ""}`}
                onClick={() => setActiveTab(key)}
              >
                {label}
              </button>
            </li>
          ))}
        </ul>

        <div className="card border-0 shadow-sm" style={{ borderTopLeftRadius: 0, borderTopRightRadius: 16, borderBottomLeftRadius: 16, borderBottomRightRadius: 16 }}>
          <div className="card-body p-3 p-md-4">

            {/* ==================== TAB 1: ย้าย Stock ==================== */}
            {activeTab === "transfer" && (
              <>
                <div className="row g-2 align-items-end mb-3">
                  <div className="col-12 col-md-5">
                    <form className="d-flex gap-2" onSubmit={handleSearch}>
                      <input
                        className="form-control form-control-sm"
                        placeholder="ค้นหารหัส / ชื่อสินค้า"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                      />
                      <button className="btn btn-primary btn-sm" type="submit">
                        ค้นหา
                      </button>
                    </form>
                  </div>
                  <div className="col-12 col-md-3">
                    <input
                      type="number"
                      min={0}
                      step={1}
                      className="form-control form-control-sm"
                      placeholder="Stock รวมต่ำกว่า..."
                      value={lowStockInput}
                      onChange={(e) => setLowStockInput(e.target.value)}
                    />
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="btn-group btn-group-sm w-100">
                      <button
                        type="button"
                        className={`btn ${stockFilterMode === "ALL" ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => setStockFilterMode("ALL")}
                      >
                        ทั้งหมด
                      </button>
                      <button
                        type="button"
                        className={`btn ${stockFilterMode === "TIKTOK_GT_ONE" ? "btn-warning" : "btn-outline-warning"}`}
                        onClick={() => setStockFilterMode("TIKTOK_GT_ONE")}
                      >
                        TikTok &gt; 1
                      </button>
                      <button
                        type="button"
                        className={`btn ${stockFilterMode === "LOW_TOTAL_WITH_TIKTOK" ? "btn-danger" : "btn-outline-danger"}`}
                        onClick={() => setStockFilterMode("LOW_TOTAL_WITH_TIKTOK")}
                      >
                        TikTok+น้อย
                      </button>
                    </div>
                  </div>
                </div>

                {/* Priority sort buttons */}
                <div className="d-flex gap-2 align-items-center flex-wrap mb-3">
                  <span className="text-muted small">เรียงขึ้นก่อน:</span>
                  {(["MAIN", "SHOP", "TIKTOK", "USA"] as const).map((loc) => {
                    const activeClass: Record<string, string> = {
                      MAIN: "btn-primary",
                      SHOP: "btn-success",
                      TIKTOK: "btn-warning",
                      USA: "btn-danger",
                    };
                    return (
                      <button
                        key={loc}
                        type="button"
                        className={`btn btn-sm ${priorityLocation === loc ? activeClass[loc] : "btn-outline-secondary"}`}
                        onClick={() => setPriorityLocation((prev) => (prev === loc ? null : loc))}
                      >
                        {loc}
                      </button>
                    );
                  })}
                  {priorityLocation && (
                    <span className="text-muted small ms-1">
                      ({priorityLocation} ขึ้นก่อน — คลิกอีกครั้งเพื่อยกเลิก)
                    </span>
                  )}
                </div>

                {error && (
                  <div className="alert alert-warning rounded-3 py-2 mb-3">{error}</div>
                )}

                {/* Stock Table */}
                <div className="table-responsive rounded-3 border" style={{ maxHeight: 560 }}>
                  <table className="table table-hover align-middle mb-0" style={{ minWidth: 560 }}>
                    <thead className="table-light sticky-top">
                      <tr>
                        <th style={{ width: 52 }}>รูป</th>
                        <th>โค้ด / ชื่อสินค้า</th>
                        {locations.map((loc) => (
                          <th key={loc.code} className="text-center" style={{ width: 80 }}>
                            {loc.code}
                          </th>
                        ))}
                        <th className="text-center" style={{ width: 70 }}>
                          รวม
                        </th>
                        <th style={{ width: 72 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading && (
                        <tr>
                          <td colSpan={3 + locations.length + 2} className="text-center text-muted py-4">
                            <div className="spinner-border spinner-border-sm me-2" role="status" />
                            กำลังโหลด...
                          </td>
                        </tr>
                      )}
                      {!loading && visibleItems.length === 0 && (
                        <tr>
                          <td colSpan={3 + locations.length + 2} className="text-center text-muted py-4">
                            {stockFilterMode === "ALL"
                              ? "ไม่พบข้อมูลสินค้า"
                              : stockFilterMode === "TIKTOK_GT_ONE"
                              ? "ไม่พบสินค้าที่มี stock TikTok มากกว่า 1 ชิ้น"
                              : lowStockThreshold == null
                              ? "กรุณาใส่เงื่อนไขขั้นต่ำก่อนใช้ตัวกรองนี้"
                              : "ไม่พบสินค้าที่เข้าเงื่อนไขตัวกรอง"}
                          </td>
                        </tr>
                      )}
                      {visibleItems.map((item) => {
                        const isLowStock =
                          lowStockThreshold != null && item.total_stock <= lowStockThreshold;
                        return (
                          <tr key={item.product_code} className={isLowStock ? "table-danger" : ""}>
                            <td>
                              {item.imageUrl ? (
                                <img
                                  src={item.imageUrl}
                                  alt={item.product_name}
                                  width={40}
                                  height={40}
                                  loading="lazy"
                                  style={{ objectFit: "cover", borderRadius: 6, background: "#f5f5f5" }}
                                  onMouseEnter={() => setPreviewImage(item.imageUrl ?? null)}
                                  onMouseLeave={() => setPreviewImage(null)}
                                  onError={(e) => {
                                    (e.currentTarget as HTMLImageElement).style.display = "none";
                                  }}
                                />
                              ) : (
                                <div
                                  className="bg-light border rounded"
                                  style={{ width: 40, height: 40 }}
                                />
                              )}
                            </td>
                            <td>
                              <div className="small text-muted lh-1">{item.product_code}</div>
                              <div className="fw-semibold" style={{ fontSize: "0.875rem" }}>
                                {item.product_name}
                              </div>
                            </td>
                            {locations.map((loc) => {
                              const qty = Number(item.stock_by_location[loc.code] ?? 0);
                              return (
                                <td key={loc.code} className="text-center">
                                  {qty === 0 ? (
                                    <span className="text-muted small">-</span>
                                  ) : (
                                    <span className={locationBadgeClass(loc.code)}>
                                      {qty.toLocaleString()}
                                    </span>
                                  )}
                                </td>
                              );
                            })}
                            <td className="text-center fw-semibold small">
                              {item.total_stock.toLocaleString()}
                            </td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary w-100"
                                onClick={() => openModal(item)}
                              >
                                ย้าย
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="small text-muted">
                    แสดง {visibleItems.length} จาก {total} รายการ
                  </div>
                  <div className="btn-group btn-group-sm">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={page <= 1 || loading}
                      onClick={() => {
                        const p = page - 1;
                        setPage(p);
                        void loadStock(p, searchQuery);
                      }}
                    >
                      ← ก่อนหน้า
                    </button>
                    <button type="button" className="btn btn-outline-secondary" disabled>
                      หน้า {page} / {totalPages}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={page >= totalPages || loading}
                      onClick={() => {
                        const p = page + 1;
                        setPage(p);
                        void loadStock(p, searchQuery);
                      }}
                    >
                      ถัดไป →
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* ==================== TAB 2: ดู Lot ==================== */}
            {activeTab === "lots" && (
              <>
                <form className="d-flex gap-2 mb-3" onSubmit={handleLotSearch}>
                  <input
                    className="form-control form-control-sm"
                    placeholder="ค้นหารหัส / ชื่อสินค้า"
                    value={lotSearchInput}
                    onChange={(e) => setLotSearchInput(e.target.value)}
                  />
                  <button className="btn btn-primary btn-sm" type="submit">
                    ค้นหา
                  </button>
                </form>

                {/* Product list */}
                <div
                  className="list-group list-group-flush rounded-3 border mb-3"
                  style={{ maxHeight: 240, overflowY: "auto" }}
                >
                  {loading && (
                    <div className="list-group-item text-center text-muted py-3">
                      <div className="spinner-border spinner-border-sm me-2" role="status" />
                      กำลังโหลด...
                    </div>
                  )}
                  {!loading && lotSearchResults.length === 0 && (
                    <div className="list-group-item text-center text-muted py-3">ไม่พบสินค้า</div>
                  )}
                  {lotSearchResults.map((item) => (
                    <button
                      key={item.product_code}
                      type="button"
                      className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${
                        selectedLotCode === item.product_code ? "active" : ""
                      }`}
                      onClick={() => selectLotProduct(item.product_code)}
                    >
                      <div>
                        <div className="fw-semibold small">{item.product_code}</div>
                        <div className="small" style={{ opacity: 0.75 }}>
                          {item.product_name}
                        </div>
                      </div>
                      <div className="d-flex gap-1 flex-wrap justify-content-end">
                        {locations.map((loc) => (
                          <span
                            key={loc.code}
                            className={locationBadgeClass(loc.code)}
                            style={{ fontSize: "0.7rem" }}
                          >
                            {loc.code}: {Number(item.stock_by_location[loc.code] ?? 0).toLocaleString()}
                          </span>
                        ))}
                      </div>
                    </button>
                  ))}
                </div>

                {/* Lot detail */}
                {!selectedLotCode && (
                  <div className="text-center text-muted py-4 small">
                    เลือกสินค้าด้านบนเพื่อดูรายละเอียด lot
                  </div>
                )}
                {selectedLotCode && (
                  <>
                    {lotLoadingCode === selectedLotCode ? (
                      <div className="text-center text-muted py-4">
                        <div className="spinner-border spinner-border-sm me-2" role="status" />
                        กำลังโหลด lot...
                      </div>
                    ) : lotErrorMap[selectedLotCode] ? (
                      <div className="alert alert-warning">{lotErrorMap[selectedLotCode]}</div>
                    ) : (lotMap[selectedLotCode] ?? []).length === 0 ? (
                      <div className="text-center text-muted py-4">
                        ยังไม่มี lot ที่คงเหลือสำหรับสินค้านี้
                      </div>
                    ) : (
                      <div className="row g-3">
                        {locationCodes.map((locCode) => {
                          const rows = selectedLotGrouped[locCode] ?? [];
                          if (rows.length === 0) return null;
                          const loc = locations.find((l) => l.code === locCode);
                          return (
                            <div key={locCode} className="col-12 col-md-4">
                              <div className={`border rounded-4 p-3 h-100 ${locationTone(locCode)}`}>
                                <div className="fw-semibold mb-2">
                                  {loc?.label ?? locCode} ({locCode})
                                </div>
                                <div className="d-grid gap-2">
                                  {rows.map((lot) => (
                                    <div key={lot.id} className="bg-white rounded-3 border px-3 py-2">
                                      <div className="d-flex justify-content-between gap-2">
                                        <div className="small fw-semibold">Lot #{lot.id}</div>
                                        <div className="small text-muted">
                                          เหลือ {lot.available_qty.toLocaleString()} / {lot.qty.toLocaleString()} ชิ้น
                                        </div>
                                      </div>
                                      <div className="small text-muted">
                                        {lot.ref_table || "-"} {lot.ref_no ? `#${lot.ref_no}` : ""}
                                      </div>
                                      <div className="small">
                                        ต้นทุน{" "}
                                        {lot.unit_cost.toLocaleString("th-TH", {
                                          style: "currency",
                                          currency: "THB",
                                        })}
                                      </div>
                                      {lot.source_movement_id && (
                                        <div className="small text-muted">
                                          มาจาก lot เดิม #{lot.source_movement_id}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {/* ==================== TAB 3: ประวัติ ==================== */}
            {activeTab === "history" && (
              <>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="text-muted small">ประวัติการย้าย stock ทั้งหมด</div>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => void loadHistory(historyPage)}
                  >
                    รีเฟรช
                  </button>
                </div>

                {historyError && (
                  <div className="alert alert-warning rounded-3">{historyError}</div>
                )}
                {historyLoading && (
                  <div className="text-center text-muted py-4">
                    <div className="spinner-border spinner-border-sm me-2" role="status" />
                    กำลังโหลด...
                  </div>
                )}

                <div className="d-grid gap-3">
                  {!historyLoading && historyItems.length === 0 && (
                    <div className="text-center text-muted py-4">
                      ยังไม่มีประวัติการย้าย stock
                    </div>
                  )}
                  {historyItems.map((history) => (
                    <div key={history.id} className="border rounded-4 p-3 bg-white">
                      <div className="d-flex flex-column flex-md-row justify-content-between gap-2 mb-2">
                        <div>
                          <div className="fw-semibold text-primary">{history.transfer_no}</div>
                          <div className="small text-muted">
                            {history.from_location_label} ({history.from_location_code}) →{" "}
                            {history.to_location_label} ({history.to_location_code})
                          </div>
                        </div>
                        <div className="small text-muted">
                          {history.created_at
                            ? new Date(history.created_at).toLocaleString("th-TH")
                            : "-"}
                        </div>
                      </div>
                      {history.note && (
                        <div className="small mb-2 text-muted">หมายเหตุ: {history.note}</div>
                      )}
                      <div className="table-responsive">
                        <table className="table table-sm align-middle mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>สินค้า</th>
                              <th className="text-end">Qty</th>
                              <th className="text-end">ต้นทุน</th>
                              <th className="text-end">Source Lot</th>
                              <th className="text-end">Dest Lot</th>
                            </tr>
                          </thead>
                          <tbody>
                            {history.items.map((hItem) => (
                              <tr key={hItem.id}>
                                <td>
                                  <div className="fw-semibold small">{hItem.product_code}</div>
                                  <div className="small text-muted">{hItem.product_name}</div>
                                </td>
                                <td className="text-end">{hItem.qty.toLocaleString()}</td>
                                <td className="text-end">
                                  {hItem.unit_cost.toLocaleString("th-TH", {
                                    style: "currency",
                                    currency: "THB",
                                  })}
                                </td>
                                <td className="text-end">{hItem.source_inventory_movement_id}</td>
                                <td className="text-end">
                                  {hItem.destination_inventory_movement_id}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="d-flex justify-content-between align-items-center mt-4">
                  <div className="small text-muted">
                    แสดง {historyItems.length} รายการ / ทั้งหมด {historyTotal} รายการ
                  </div>
                  <div className="btn-group btn-group-sm">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={historyPage <= 1 || historyLoading}
                      onClick={() => {
                        const p = historyPage - 1;
                        setHistoryPage(p);
                        void loadHistory(p);
                      }}
                    >
                      ← ก่อนหน้า
                    </button>
                    <button type="button" className="btn btn-outline-secondary" disabled>
                      หน้า {historyPage} / {totalHistoryPages}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={historyPage >= totalHistoryPages || historyLoading}
                      onClick={() => {
                        const p = historyPage + 1;
                        setHistoryPage(p);
                        void loadHistory(p);
                      }}
                    >
                      ถัดไป →
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ==================== Transfer Modal ==================== */}
      {modalOpen && modalProduct && activeTransfer && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div
            className="card border-0 shadow-lg rounded-4 mx-3"
            style={{ width: "100%", maxWidth: 460 }}
          >
            <div className="card-body p-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">ย้าย Stock</h5>
                <button type="button" className="btn-close" onClick={closeModal} />
              </div>

              {/* Product info */}
              <div className="mb-3">
                <div className="small text-muted">{modalProduct.product_code}</div>
                <div className="fw-semibold">{modalProduct.product_name}</div>
              </div>

              {/* Stock summary per location */}
              <div className="d-flex gap-2 mb-3">
                {locations.map((loc) => {
                  const stockQty = Number(modalProduct.stock_by_location[loc.code] ?? 0);
                  const isDraggable = stockQty > 0;
                  const isDragging = dragFromLoc === loc.code;
                  const isDropTarget = dragOverLoc === loc.code && dragFromLoc !== loc.code;
                  const isSelected = activeTransfer.fromLocationCode === loc.code;
                  return (
                    <div
                      key={loc.code}
                      draggable={isDraggable}
                      onDragStart={() => { if (isDraggable) setDragFromLoc(loc.code); }}
                      onDragEnd={() => { setDragFromLoc(null); setDragOverLoc(null); }}
                      onDragOver={(e) => { if (dragFromLoc && dragFromLoc !== loc.code) { e.preventDefault(); setDragOverLoc(loc.code); } }}
                      onDragLeave={() => setDragOverLoc(null)}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (!dragFromLoc || dragFromLoc === loc.code) return;
                        setActiveTransfer((prev) => prev ? {
                          ...prev,
                          fromLocationCode: dragFromLoc,
                          toLocationCode: loc.code,
                          costReceived: loc.code === "USA",
                          costReturned: dragFromLoc === "USA",
                          qty: 1,
                        } : prev);
                        setDragFromLoc(null);
                        setDragOverLoc(null);
                        setTimeout(() => qtyInputRef.current?.focus(), 50);
                      }}
                      className={`flex-fill text-center rounded-3 py-2 border ${
                        isDropTarget
                          ? "border-warning border-3 shadow " + locationTone(loc.code)
                          : isSelected
                          ? locationTone(loc.code) + " border-2"
                          : locationTone(loc.code)
                      }`}
                      style={{
                        cursor: isDraggable ? "grab" : "default",
                        opacity: isDragging ? 0.45 : isDraggable ? 1 : 0.35,
                        transition: "opacity 0.15s, box-shadow 0.15s",
                      }}
                    >
                      <div className="small text-muted">{loc.code}</div>
                      <div className="fw-bold">{stockQty.toLocaleString()}</div>
                      {isDraggable && (
                        <div style={{ fontSize: 10, color: "#888", lineHeight: 1 }}>⠿ ลาก</div>
                      )}
                    </div>
                  );
                })}
              </div>

              <form onSubmit={submitTransfer}>
                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <label className="form-label small mb-1 fw-semibold">จาก</label>
                    <select
                      className="form-select form-select-sm"
                      value={activeTransfer.fromLocationCode}
                      onChange={(e) => {
                        const newFrom = e.target.value;
                        setActiveTransfer((prev) => {
                          if (!prev) return prev;
                          const newTo =
                            prev.toLocationCode === newFrom
                              ? locations.find((l) => l.code !== newFrom)?.code ?? ""
                              : prev.toLocationCode;
                          return {
                            ...prev,
                            fromLocationCode: newFrom,
                            toLocationCode: newTo,
                            qty: 1,
                            costReceived: newTo === "USA",
                            costReturned: newFrom === "USA",
                          };
                        });
                      }}
                    >
                      {locations.map((loc) => (
                        <option key={loc.code} value={loc.code}>
                          {loc.label} ({loc.code})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label small mb-1 fw-semibold">ไป</label>
                    <select
                      className="form-select form-select-sm"
                      value={activeTransfer.toLocationCode}
                      onChange={(e) => {
                        const newTo = e.target.value;
                        setActiveTransfer((prev) =>
                          prev
                            ? { ...prev, toLocationCode: newTo, costReceived: newTo === "USA" }
                            : prev
                        );
                      }}
                    >
                      {locations
                        .filter((l) => l.code !== activeTransfer.fromLocationCode)
                        .map((loc) => (
                          <option key={loc.code} value={loc.code}>
                            {loc.label} ({loc.code})
                          </option>
                        ))}
                    </select>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label small mb-1 fw-semibold">
                    จำนวน{" "}
                    <span className="text-muted fw-normal">
                      (สต็อกต้นทาง: {currentFromStock.toLocaleString()}{" "}
                      {getSafeUnitLabel(modalProduct.unit)})
                    </span>
                  </label>
                  <input
                    ref={qtyInputRef}
                    type="number"
                    min={0}
                    max={currentFromStock}
                    step={1}
                    className="form-control form-control-sm"
                    value={activeTransfer.qty}
                    onChange={(e) =>
                      setActiveTransfer((prev) =>
                        prev ? { ...prev, qty: Number(e.target.value) } : prev
                      )
                    }
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label small mb-1 fw-semibold">หมายเหตุ</label>
                  <input
                    className="form-control form-control-sm"
                    placeholder="เช่น ย้ายไปลงสต็อก TikTok"
                    value={activeTransfer.note}
                    onChange={(e) =>
                      setActiveTransfer((prev) =>
                        prev ? { ...prev, note: e.target.value } : prev
                      )
                    }
                  />
                </div>

                {/* PMN-CRITICAL(/StockTransferPage): USA cost toggles can create or reverse backend order side effects. */}
                {/* USA cost_returned toggle — ย้ายออกจาก USA */}
                {activeTransfer.fromLocationCode === "USA" && (
                  <div className="mb-3 p-3 rounded-3 border border-warning-subtle bg-warning-subtle">
                    <div className="form-check form-switch mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="costReturnedSwitch"
                        checked={activeTransfer.costReturned}
                        onChange={(e) =>
                          setActiveTransfer((prev) =>
                            prev ? { ...prev, costReturned: e.target.checked } : prev
                          )
                        }
                      />
                      <label className="form-check-label small fw-semibold" htmlFor="costReturnedSwitch">
                        คืนเงินทุนด้วย
                      </label>
                    </div>
                    <div className="small text-muted mt-1">
                      {activeTransfer.costReturned
                        ? "✅ บันทึกว่าคืนเงินทุนสำหรับของที่ส่งกลับ"
                        : "⬜ ไม่คืนเงินทุน (ของกลับแต่เงินไม่กลับ)"}
                    </div>
                  </div>
                )}

                {/* USA cost_received toggle — ย้ายเข้า USA */}
                {activeTransfer.toLocationCode === "USA" && (
                  <div className="mb-3 p-3 rounded-3 border border-danger-subtle bg-danger-subtle">
                    <div className="form-check form-switch mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="costReceivedSwitch"
                        checked={activeTransfer.costReceived}
                        onChange={(e) =>
                          setActiveTransfer((prev) =>
                            prev ? { ...prev, costReceived: e.target.checked } : prev
                          )
                        }
                      />
                      <label className="form-check-label small fw-semibold" htmlFor="costReceivedSwitch">
                        นับว่าได้รับเงินค่าต้นทุนแล้ว
                      </label>
                    </div>
                    <div className="small text-muted mt-1">
                      {activeTransfer.costReceived
                        ? "✅ บันทึกว่าต้นทุนสินค้าชุดนี้ได้รับเงินคืนแล้ว"
                        : "⬜ ยังไม่นับเป็นรายรับ รอบันทึกยอดขายทีหลัง"}
                    </div>
                  </div>
                )}

                {transferError && (
                  <div className="alert alert-warning py-2 px-3 small mb-3">{transferError}</div>
                )}

                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary flex-fill"
                    onClick={closeModal}
                  >
                    ยกเลิก
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary flex-fill"
                    disabled={
                      transferSubmitting ||
                      currentFromStock <= 0 ||
                      !activeTransfer ||
                      activeTransfer.qty <= 0
                    }
                  >
                    {transferSubmitting ? "กำลังย้าย..." : "ยืนยันย้าย"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview */}
      {previewImage && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: "rgba(0,0,0,0.72)", zIndex: 2200, pointerEvents: "none" }}
        >
          <img
            src={previewImage}
            alt="product preview"
            style={{
              maxWidth: "90vw",
              maxHeight: "85vh",
              borderRadius: 16,
              boxShadow: "0 18px 60px rgba(0,0,0,0.35)",
              background: "#fff",
            }}
          />
        </div>
      )}
    </div>
  );
}
